import { Slab } from '../../render/parts'
import { FramedArt } from '../parts/props'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * WALL ITEM — origin on the wall plane, back face at z = 0, growing into +Z.
 *
 * `FramedArt` is centred on z = 0 (its 26 mm frame slab spans z ∈ [-0.013,
 * 0.013]), so every frame here is pushed forward by FRAME_HALF to land the back
 * face exactly on the wall.
 */
const FRAME_DEPTH = 0.026
const FRAME_HALF = FRAME_DEPTH / 2

/** Declared bounding width — driven by the widest variant (the triptych). */
const SPAN = 1.1

/**
 * Frame with a charcoal field instead of the accent one. `FramedArt` always
 * paints its inner field with the accent tone and only a hex accent may be
 * passed through, so the "equipment grey" print is built here from primitives
 * that mirror FramedArt's own proportions exactly.
 */
function InkFrame({ width, height }: { width: number; height: number }) {
  return (
    <group>
      <Slab tone="shell" size={[width, height, FRAME_DEPTH]} radius={0.006} rotation={[Math.PI / 2, 0, 0]} />
      <Slab
        tone="dark"
        size={[width - 0.12, height - 0.14, 0.006]}
        radius={0.004}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, 0, 0.015]}
      />
    </group>
  )
}

/**
 * Graphic band laid over the accent field — the second plane that keeps a lone
 * frame from reading as a flat yellow rectangle. It bites 1 mm into the field
 * so the two overlap instead of sharing a plane.
 */
function GraphicBand({ width, height, y }: { width: number; height: number; y: number }) {
  return (
    <Slab
      tone="dark"
      size={[width, height, 0.005]}
      radius={0.003}
      rotation={[Math.PI / 2, 0, 0]}
      position={[0, y, 0.0195]}
    />
  )
}

function ArtFrame({ accent, variant = 'vertical' }: AssetContext) {
  if (variant === 'trio') {
    // Three small frames in a row, centred inside the declared 1.1 m span.
    const w = 0.3
    const h = 0.36
    const pitch = 0.36
    return (
      <group position={[0, 0, FRAME_HALF]}>
        <group position={[-pitch, 0, 0]}>
          <InkFrame width={w} height={h} />
        </group>
        <FramedArt width={w} height={h} accent={accent} />
        <group position={[pitch, 0, 0]}>
          <InkFrame width={w} height={h} />
        </group>
      </group>
    )
  }

  const horizontal = variant === 'horizontal'
  const w = horizontal ? 0.62 : 0.44
  const h = horizontal ? 0.42 : 0.56

  return (
    <group position={[0, 0, FRAME_HALF]}>
      <FramedArt width={w} height={h} accent={accent} />
      <GraphicBand
        width={(w - 0.12) * 0.62}
        height={(h - 0.14) * 0.24}
        y={-(h - 0.14) * 0.26}
      />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'art-frame',
  name: 'Cuadro',
  category: 'decor',
  footprint: [SPAN, 0.04],
  height: 0.58,
  anchor: 'wall',
  mountHeight: 1.55,
  accentable: true,
  variants: [
    { id: 'vertical', name: 'Vertical' },
    { id: 'horizontal', name: 'Horizontal' },
    { id: 'trio', name: 'Trío' },
  ],
  keywords: ['cuadro', 'lámina', 'arte', 'marco', 'art', 'frame', 'poster', 'picture'],
  Component: ArtFrame,
}
